// data.ts — sample data + economics helpers for the CMS.
// Context: 한부금속 — 금속 정밀가공 / 제관 업체.

import type { CncQuoteResult, TreatmentUnit } from "@/lib/cost/cncQuote";
import type { ProductShape } from "@/lib/cost/weightCalc";
import type { QuoteAttachment } from "./quoteFiles";

export type StatusKey = "draft" | "submitted" | "won" | "lost" | "hold";

export interface StatusDef {
  key: StatusKey;
  label: string;
  en: string;
  color: string;
  bg: string;
}

export interface LineItem {
  id: string | number;
  item: string;
  spec: string;
  unit: string;
  qty: number;
  mat: number;
  proc: number;
  etc: number;
  margin: number;
  note: string;
}

export interface QuoteVersion {
  v: string;
  at: string;
  by: string;
  note: string;
}

export interface QuoteLog {
  who: string;
  at: string;
  what: string;
}

export interface Quote {
  id: string;
  project: string;
  customerId: string;
  status: StatusKey;
  owner: string;
  ownerUid: string; // Firebase UID of the creator — read-only, server-managed.
  approver: string;
  lossReason?: string;
  created: string;
  updated: string;
  sop: string; // 양산 시점(Start of Production), YYYY-MM-DD
  purpose: string; // 제출 용도 — RFQ용/업체선정용/재견적/샘플견적
  items: LineItem[];
  quoteType: "general" | "cnc";
  cnc: CncPart[];
  attachments: QuoteAttachment[];
  remark?: string; // 비고 — 견적 전반에 대한 자유 입력 메모.
  opinion?: string; // 담당자 의견 — 견적서(PDF) 하단 자유 입력. 기본 "".
  customerDoc?: CustomerDoc; // 고객 제출용 견적서(이슈 #110)의 직접 입력 값 영속. 미설정 시 연동값으로 프리필.
  versions: QuoteVersion[];
  logs: QuoteLog[];
}

// 고객 제출용 견적서(이슈 #110) — 직접 입력 값만 영속. 연동값(견적번호·제목·작성일/자·
// 품목 연동 컬럼)은 저장하지 않고 매번 견적에서 프리필. 단, 사용자가 연동값을 덮어쓰면
// overrides 로 보존해 재출력 안정성을 유지한다. items 는 표시 순서와 1:1(인덱스 매칭).
export interface CustomerDocItem {
  image: string; // 직접 입력 — 첨부 이미지 키(quotes/files) 또는 URL/텍스트.
  etc: string; // 직접 입력 — 기타.
}
export interface CustomerDoc {
  recipient: string; // 수신 — 직접 입력.
  reference: string; // 참조 — 직접 입력.
  remark: string; // 비고 — quote.remark 와 연동(표지 저장 시 함께 기록). 표시값은 quote.remark 기준.
  items: CustomerDocItem[];
  // 연동값 사용자 override(미설정 키는 연동값 사용). 모든 항목 수정 가능 요구사항 충족용.
  overrides?: Record<string, string>;
}

export interface Contact {
  name: string;
  tel: string;
}

export interface Customer {
  id: string;
  name: string;
  biz: string;
  ceo: string;
  contacts: Contact[];
}

export interface Process {
  id: string;
  name: string;
  unit: string;
  rate: number;
}

export interface Material {
  id: string;
  name: string;
  spec: string;
  unit: string;
  baseMat: number;
}

export interface Staff {
  id: string;
  name: string;
}

export interface PriceHistoryPoint {
  date: string;
  customer: string;
  unit: number;
  status: StatusKey;
}

export interface MonthlyPoint {
  m: string;
  quotes: number;
  won: number;
  amount: number;
}

// ----- CNC 마스터 (CNC 견적 세부원가 기준 정보) -----
export interface CncRawMaterial {
  id: string;
  name: string;
  rawUnitPrice: number; // 원소재 단가 (₩/g)
  scrapUnitPrice: number; // 스크랩 단가 (₩/g)
  density: number; // 비중 (g/cm³) — 예 7.87
  gPerMm3: number; // 단중 (g/mm³) = 비중/1000 — 예 0.00787
  recoveryRate?: number; // 스크랩 회수율 (%) — 재질별, 미보유 시 기본 90 (내용추가18)
}

export interface CncSurfaceTreatment {
  id: string;
  name: string;
  unitPrice: number; // 단가 (₩/g)
}

export interface CncLaborRate {
  id: string;
  process: string; // 공정명
  equipment: string; // 장비명
  headcount: number; // 작업인원
  hourlyRate: number; // 시간당임율 (₩/hr)
}

export interface CncMachineExpense {
  id: string;
  process: string; // 공정명
  equipment: string; // 장비명
  equipmentPrice: number; // 장비가액 (₩)
  areaM2: number; // 설치면적 (㎡)
  buildingYears: number; // 건물년수
  equipmentYears: number; // 설비년수
  yearlyWorkDays: number; // 년작업일수
  dailyWorkHours: number; // 일작업시간
  auxEquipmentRatio: number; // 부대설비비율
  buildingCost: number; // 건축비용 (₩/㎡)
  repairRatio: number; // 수선비율
  powerKw: number; // 전력량 (kW)
  loadRatio: number; // 부하율
  powerUnitPrice: number; // 전력단가 (₩/kWh)
  indirectExpenseRatio: number; // 간접경비율
}

export interface CncSettings {
  allowance: number; // 여유율
  generalAdminRate: number; // 일반관리비율
  materialAdminRate: number; // 재료관리비율
  indirectExpenseRatio: number; // 간접경비율
}

// CNC 견적 폼 상태(저장 단위). selection = 폼 source of truth(문자열/ids),
// result = 저장 시점 산출 스냅샷. @/lib/cost/cncForm 가 selection→result 도출.
export interface CncProcessSelection {
  laborRateId: string;
  ctSec: string;
  machineRate: string; // 기계경비(₩/hr) — 자동 채움 + override
  headcount: number; // 저장 시점 스냅샷 — labor 마스터에서 resolve
  hourlyLaborRate: number; // 저장 시점 스냅샷 — labor 마스터에서 resolve
  allowance?: number; // 공정별 여유율 — 미지정 시 part 기본(sel.allowance) fallback
}
// 표면처리/열처리/기타비 한 항목(라인) — 폼 상태(문자열 단가).
export interface CncTreatmentLine {
  id: string; // 로컬 key
  label: string; // 표시명 (표면처리는 마스터명, 열처리/기타는 자유 입력)
  surfaceId?: string; // 표면처리 라인은 마스터 id 연결 (열처리/기타는 미사용)
  unit: TreatmentUnit; // "kg" | "ea" | "g"(legacy)
  unitPrice: string; // ₩/kg, ₩/ea 또는 ₩/g
}

// 제품중량 계산 팝업 표의 한 행(저장용 — 문자열). shape 는 weightCalc 의 ProductShape.
export interface CncProductWeightRow {
  shape: ProductShape;
  spec: string; // 규격명
  outer: string; // 외경/가로 (mm)
  inner: string; // 내경 (mm)
  length: string; // 길이 (mm)
  gPerMm3: string; // 단중 (g/mm³)
  qty: string; // 수량 (EA)
}

export type ProfitMode = "amount" | "rate";
export type CostType = "inhouse" | "outsourced";

// 연도별 물량 — "yYYYY" → 수량 문자열. 연도는 동적(기본 2026~2030, +연도 추가 가능).
// 연평균은 저장하지 않고 selectionAvgInput 으로 계산한다(레거시 avg 키가 있어도 무시).
export type CncPartVolumes = Record<string, string>;

export interface CncPartSelection {
  itemName: string; // 품목명
  partNo?: string; // 품번 ★ — 사용자 직접 입력(품목명 옆 박스). 구 견적 스냅샷엔 없음 → 기본 "".
  partImage?: string; // 파트 이미지 — 오브젝트 스토리지 키(quotes/…). 견적상세 도면/사진·제출견적서 이미지 컬럼 연동. 구 견적 미보유.
  spec?: string; // 규격 ★ — 사용자 직접 입력(견적상세 표). 기본 미입력("").
  partQuoteType: string; // 견적유형 (파트별)
  volumes: CncPartVolumes; // 연도별 물량 정보 (EA/년)
  usgQty: string; // USG 수량
  materialId: string;
  outerDiameter: string; // 외경 (mm) — 절단 LOSS 자동조회 + 팝업 연동
  productLength: string; // 제품 길이 (mm) — 투입중량 계산 팝업 입력 보존
  inputWeight: string; // 투입중량 (g)
  productWeight: string; // 제품중량 (g)
  rawPrice: string; // 원소재 단가 (₩/g)
  scrapPrice: string; // 스크랩 단가 (₩/g)
  scrapWeight: string; // 스크랩중량 (g) — 미입력 시 투입−제품 자동
  // 스크랩 회수율 (%) — 내용추가18. 새 견적은 재질 기본값(90)으로 채움. 필드 없음(회수율 도입 전
  // 저장 견적) 또는 빈 값 = 100% 로 계산해 기존 견적 금액이 바뀌지 않게 한다.
  scrapRecoveryRate?: string;
  // 투입중량 계산 팝업의 원자재 전장/잔봉 길이 (mm) — 견적별 저장. 미보유 시 2,500 / 250 기본.
  stockLength?: string;
  remnantLength?: string;
  rows: CncProcessSelection[];
  surfaceLines: CncTreatmentLine[]; // 표면처리비
  heatLines: CncTreatmentLine[]; // 열처리비
  etcLines: CncTreatmentLine[]; // 기타비
  profitMode: ProfitMode; // 이윤 입력 모드 — 금액(₩/ea) / 마진율(%)
  profit: string; // profitMode==="amount" 일 때 ₩/ea
  profitRate: string; // profitMode==="rate" 일 때 % (판매가 기준 영업이익률)
  costType: CostType; // 내작(17%) / 외작(10%) — generalAdminRate 결정
  moldTotal: string;
  amortize: boolean;
  productionQty: string;
  miscCost: string;
  productWeightRows?: CncProductWeightRow[]; // 제품중량 계산 팝업 표(선택 — 저장 시 보존)
  allowance: number; // 저장 시점 스냅샷 — settings 에서 resolve (공정 기본 여유율)
  generalAdminRate: number; // 저장 시점 스냅샷 — costType 에서 resolve
  materialAdminRate: number; // 저장 시점 스냅샷 — settings 에서 resolve
}
export interface CncPart {
  selection: CncPartSelection;
  result: CncQuoteResult;
}

// ----- Formatters -----
export const fmt = (n: number | null | undefined): string =>
  n == null || Number.isNaN(n) ? "—" : Math.round(n).toLocaleString("ko-KR");
export const fmtKRW = (n: number | null | undefined): string => `${fmt(n)} 원`;
// 소수점 둘째자리 고정 표시 — CNC 재료비/원가 화면 전용(시트3·4 요청: 242.00 처럼 후행 0 까지
// 항상 2자리 노출). min·max 를 2 로 맞춰 "242" 가 아닌 "242.00" 으로 보인다. 다른 화면(대시보드·
// 리포트)의 정수 표기(fmt/fmtKRW)와는 분리 — 전역 반올림 정책은 그대로 둔다.
export const fmtDec = (n: number | null | undefined): string =>
  n != null && Number.isFinite(n)
    ? n.toLocaleString("ko-KR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "—";
export const fmtKRWDec = (n: number | null | undefined): string => `${fmtDec(n)} 원`;
// 소수점 첫째자리 표시 — 견적상세 분해표 재료비~내부단가 전용(내용추가5 R1: 1원 미만이
// 정수 반올림으로 0 이 되는 문제 해결). 예: 0.3 → "0.3", 63 → "63.0".
export const fmt1 = (n: number | null | undefined): string =>
  n != null && Number.isFinite(n)
    ? n.toLocaleString("ko-KR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    : "—";
export const fmtComp = (n: number | null | undefined): string => {
  if (n == null || Number.isNaN(n)) return "—";
  const a = Math.abs(n);
  if (a >= 1e12) return `${(n / 1e12).toFixed(2)}조`;
  if (a >= 1e8) return `${(n / 1e8).toFixed(1)}억`;
  if (a >= 1e4) return `${Math.round(n / 1e4).toLocaleString("ko-KR")}만`;
  return fmt(n);
};
export const pct = (n: number | null | undefined, d = 1): string =>
  n == null || Number.isNaN(n) ? "—" : `${n.toFixed(d)}%`;

// ----- Date helpers -----
// "오늘" 기준 동작은 컴포넌트 경계에서 만든 Date 를 받아 여기서 계산한다.
// (toISOString() 은 UTC 라 KST 자정~09시 사이 하루가 밀림 — 로컬 필드 사용.)
export function isoDate(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

export function addDays(d: Date, days: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + days);
  return out;
}

// 최근 n개월 필터 옵션 — 이번 달부터 역순. value "YYYY-MM", label "YYYY년 M월".
export function recentMonths(d: Date, n: number): { value: string; label: string }[] {
  return Array.from({ length: n }, (_, i) => {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    return {
      value: `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`,
      label: `${m.getFullYear()}년 ${m.getMonth() + 1}월`,
    };
  });
}

// ----- Status model -----
export const STATUS: Record<StatusKey, StatusDef> = {
  draft: {
    key: "draft",
    label: "작성 중",
    en: "DRAFT",
    color: "var(--text-muted)",
    bg: "rgba(179,179,179,0.14)",
  },
  submitted: {
    key: "submitted",
    label: "제출 완료",
    en: "SUBMITTED",
    color: "var(--text-announcement)",
    bg: "rgba(83,157,245,0.15)",
  },
  won: {
    key: "won",
    label: "수주 성공",
    en: "WON",
    color: "var(--num-positive)",
    bg: "rgba(0,208,130,0.14)",
  },
  lost: {
    key: "lost",
    label: "수주 실패",
    en: "LOST",
    color: "var(--text-negative)",
    bg: "rgba(243,114,127,0.15)",
  },
  hold: {
    key: "hold",
    label: "보류",
    en: "HOLD",
    color: "var(--text-warning)",
    bg: "rgba(255,164,43,0.15)",
  },
};

// Static UI list of units (not master data — kept here intentionally).
export const UNITS = ["EA", "SET", "m", "kg"];

// ----- Line item economics -----
// 내부단가 = 재료비 + 가공비 + 기타
// 제출단가 = 내부단가 ÷ (1 − 마진율/100)  ← Margin(판매가 기준 영업이익률) 방식.
// 마진율은 판매가 대비 영업이익률이라 (제출−내부)/제출×100 == 입력 마진율 로 자기일관.
//   예) 내부 150, 마진 10% → 150 ÷ 0.90 = 166.67 → 표시 167.
export interface LineCalc {
  internal: number;
  submit: number;
  internalTotal: number;
  submitTotal: number;
  profitTotal: number;
  profitRate: number;
}
// 판매가 = 내부원가 ÷ (1 − 마진율/100). 마진 ≥ 100% 는 정의 불가 → 내부원가 반환(분모 0/음수 가드).
export function applyMargin(internal: number, marginPct: number): number {
  const divisor = 1 - marginPct / 100;
  return divisor > 0 ? internal / divisor : internal;
}
export function lineCalc(row: Pick<LineItem, "mat" | "proc" | "etc" | "margin" | "qty">): LineCalc {
  const internal = (row.mat || 0) + (row.proc || 0) + (row.etc || 0);
  const submit = applyMargin(internal, row.margin || 0);
  const qty = row.qty || 0;
  return {
    internal,
    submit,
    internalTotal: internal * qty,
    submitTotal: submit * qty,
    profitTotal: (submit - internal) * qty,
    profitRate: submit > 0 ? ((submit - internal) / submit) * 100 : 0,
  };
}

export interface QuoteTotals {
  internal: number;
  submit: number;
  profit: number;
  profitRate: number;
}
export function quoteTotals(items: LineItem[]): QuoteTotals {
  let internal = 0;
  let submit = 0;
  for (const r of items) {
    const c = lineCalc(r);
    internal += c.internalTotal;
    submit += c.submitTotal;
  }
  const profit = submit - internal;
  return { internal, submit, profit, profitRate: submit > 0 ? (profit / submit) * 100 : 0 };
}

export const MARGIN_FLOOR = 5; // 가드레일: 영업이익률 5% 미만 경고

// 편집 그리드 프리필 — 저장된 품목의 id 는 서버 유래(string 가능)라 그리드용
// 순번 id(1..n) 로 재부여한다. 품목이 없으면 신규 화면과 같은 빈 행 1개.
export type GridLineItem = Omit<LineItem, "id"> & { id: number };
export const emptyGridRow = (id: number): GridLineItem => ({
  id,
  item: "",
  spec: "",
  unit: "EA",
  qty: 1,
  mat: 0,
  proc: 0,
  etc: 0,
  margin: 15,
  note: "",
});
export function itemsToGridRows(items: LineItem[]): GridLineItem[] {
  if (!items.length) return [emptyGridRow(1)];
  return items.map((it, i) => ({ ...it, id: i + 1 }));
}

// Resolve a customer name from a fetched list (replaces the old CUSTOMERS-backed
// `customerName`). Screens pass the list from useCustomers().
export const customerNameFrom = (customers: Customer[], id: string): string =>
  customers.find((c) => c.id === id)?.name || "—";

// 고객사 담당자 이름 목록 — 빈 이름은 제외. 견적 담당자(Owner) 드롭다운 옵션 등에 사용.
export const contactNames = (c: Customer | undefined): string[] =>
  (c?.contacts ?? []).map((ct) => ct.name.trim()).filter((n) => n.length > 0);

// 견적 담당자(Owner) 드롭다운 옵션 — 선택한 고객사의 담당자 목록.
// 기존 견적이 보유한 owner 값이 목록에 없으면(예: 과거 담당자) 그 값도 포함해 표시가 유지되게 한다.
export const ownerOptions = (c: Customer | undefined, current: string): string[] => {
  const names = contactNames(c);
  const cur = current.trim();
  return cur && !names.includes(cur) ? [...names, cur] : names;
};

// 담당자 한 줄 요약 — "이름 · 연락처 / 이름 · 연락처". 비어 있으면 "—".
export const formatContacts = (c: Customer | undefined): string => {
  const parts = (c?.contacts ?? [])
    .map((ct) => [ct.name.trim(), ct.tel.trim()].filter(Boolean).join(" · "))
    .filter((s) => s.length > 0);
  return parts.length ? parts.join(" / ") : "—";
};
